import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {animate, style, transition, trigger} from "@angular/animations";
import {filter, first, Subscription, timer} from 'rxjs';

import {CITY2, CITY2_DEFAULT, GetRegionName, isCity2, REGION} from '../shared/defines/gukeo.const';
import {
  DIALECT_SEARCH_PLACEHOLDER,
  HISTORY_KEY_SEARCH_DIALECT,
  HISTORY_KEY_VIEW_DIALECT,
  LIST_TYPE,
  SearchParam
} from "../shared/defines/client.const";
import {CITY2_INFO_MAP, GetRegionByText, MENU_PAGE} from '../shared/defines/desktop.const';
import {GlobalEventService} from '../shared/services/global-event/global-event.service';
import {RedPipeService} from "../shared/services/red-pipe/red-pipe.service";
import {generateUrlWithQueryParams} from "../shared/defines/client.func";


const MSG_POPUP_TIMEOUT = 4000;  // 4초

@Component({
  selector: 'app-dialect',
  templateUrl: './dialect.component.html',
  styleUrls: ['./dialect.component.scss'],
  animations: [
    trigger('msgPopupFadeOut', [
      transition(':leave', [
        animate('0.5s', style({ opacity: 0 }))
      ])
    ])
  ],
})
export class DialectComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly REGION = REGION;
  protected readonly HISTORY_KEY_SEARCH_DIALECT = HISTORY_KEY_SEARCH_DIALECT;
  protected readonly HISTORY_KEY_VIEW_DIALECT = HISTORY_KEY_VIEW_DIALECT;
  protected readonly CITY2_DEFAULT = CITY2_DEFAULT;

  public selectedMenu: MENU_PAGE = MENU_PAGE.DIALECT;
  private type: LIST_TYPE | null = null;
  protected subPath: string | null = null;

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null = null;

  private queryParams$?: Subscription;
  private params$?: Subscription;
  private router$?: Subscription;  // 구독을 저장할 변수 추가

  // 검색창에 넣는 값
  protected keyword: string | null = null;
  protected placeholder = DIALECT_SEARCH_PLACEHOLDER;
  protected scrollTop = true;

  // 지역 선택 팝업창 관련
  public selectedCity: CITY2 = CITY2_DEFAULT;  // 선택 지역
  protected selectedRegion: REGION = REGION.ALL;    // 왼쪽 지도의 줌 한 지역

  // 지역 버튼 선택 안내 메시지 팝업창 표시 여부
  protected showMsgPopup = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private globalEventSvc: GlobalEventService,
    private redPipeSvc: RedPipeService,
  ) {
    // super(globalEventSvc);

    /******************************************************************************************
     * 주소 뒤의 파라미터를 받아오는 곳.
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        console.log('DialectComponent::constructor() queryParams =', params);

        this.queryParams = params;

        // 지역 설정
        let regionByCity = REGION.ALL;
        if ('city' in params) {
          const city = params['city'];
          if (isCity2(city)) {
            this.selectedCity = city;
            regionByCity = CITY2_INFO_MAP.get(city)?.region ?? REGION.ALL;
          }
          else {
            console.warn('DialectComponent::constructor() invalid city =', params['city']);
            this.selectedCity = CITY2_DEFAULT;
          }
        }
        else {
          this.selectedCity = CITY2_DEFAULT;
        }

        // city보다 region 파라미터가 우선순위가 높다.
        if ('region' in params) {
          const selectedRegion = GetRegionByText(params['region']);
          if (selectedRegion) {
            this.selectedRegion = selectedRegion.region;
          }
          else {
            console.warn('DialectComponent::constructor() invalid region =', params['region']);
            this.selectedRegion = regionByCity;
          }
        }
        else {
          this.selectedRegion = regionByCity;
        }

        // 검색창 설정
        this.keyword = params['keyword'] ?? null;
      });


    /******************************************************************************************
     * 라우터 파라미터를 읽어오는 곳
     * 처음 이 페이지에 들어올 때 불려진다.
     * url이 변경되어도 불린다.
     * queryParams가 바뀐 것(주소뒤의 파리미터가 변경된 경우)으로는 여기가 불려지지 않는다.
     */
    this.params$ = route.params.subscribe(params => {
      console.log('DialectComponent::constructor() params =', params, this.queryParams);

      // this.type = params['type'] ?? null; // 들어오지 않는다.
      // NOTE: type은 하위 라우터의 파라미터이므로, 상위 페이지에서는 params 에 type을 넣어주지 않는다.

      this.init();
    });


    /******************************************************************************************
     * 하부 페이지 주소 받아오는 곳
     * 하부 페이지의 이동이 완료되면 불려진다. (NavigationEnd)
     */
    this.router$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(event => {
      const path = this.route.firstChild?.routeConfig?.path ?? null;
      this.subPath = path ? path.split('/')[0] : null;  // 파라미터를 잘라낸다.
      // console.log('DialectComponent::constructor() sub path = ', this.subPath, path, this.route.firstChild);

      if (this.subPath !== 'region') {
        this.selectedCity = CITY2_DEFAULT; // 영상 (region) 페이지가 아닌 경우 지역을 초기화한다. 파라미터로 들어왔다면 파라미터 처리하는 곳에서 다시 입력된다.
        this.type = LIST_TYPE.SERVER;
      }

      // 메뉴 페이지가 아닌 경우 메시지 팝업창을 표시하지 않는다.
      if (this.subPath !== 'menu') {
        this.showMsgPopup = false;
      }
    });

    /**
     * 하위 라우터 파라미터를 읽어옴
     */
    this.route.firstChild?.params.subscribe(params => {
      console.warn('DialectComponent::constructor() firstChild params =', params);

      if ('type' in params) {
        this.type = params['type'];
      }
    });
  }


  /**
   * 저장된 searchParam(라우팅에서 전달받은 옵션)을 이용해서 페이지를 재 초기화하고 다시 로딩한다.
   */
  private init(): void {
    console.log('DialectComponent::init() queryParams =', this.queryParams);
  }


  public ngOnInit(): void {
    // 시간이 지나면 사라진다.
    // this.showMsgPopup && timer(MSG_POPUP_TIMEOUT).subscribe(() => this.showMsgPopup = false);



    // if (this.main) {
    //   this.InitFocusMain(this.main);
    // }
    // else {
    //   console.error('DialectComponent::ngOnInit() main is null');
    // }

  }

  ngAfterViewInit() {
    // 클릭이 발생하면 사라진다.
    timer(500).subscribe(() => {
      this.globalEventSvc.Click$.pipe(
        first(),
      ).subscribe(() => this.showMsgPopup = false);
    });
  }

  public ngOnDestroy(): void {
    // this.ExitFocusMain();
    this.queryParams$?.unsubscribe();
    this.params$?.unsubscribe();
    this.router$?.unsubscribe();
  }

  public onGoHistory(): void {
    this.type = null; // 생성자의 queryParams 쪽으로 init()가 불려지는 것을 막음.
    this.router.navigate(['/dialect-list', LIST_TYPE.HISTORY]).then();
  }

  /****************************************************************************
   * 검색 처리
   ***************************************************************************/

  public onSearch(param: SearchParam): void {
    console.log('DialectComponent::onSearch() =', param, this.type);

    this.router.navigate(['/dialect-list', LIST_TYPE.SERVER], { queryParams: param }).then();
  }

  protected onCityChange(city2: CITY2): void {
    console.log('DialectComponent::onRegionChange() city2 =', city2, this.type);

    if (isCity2(city2)) {
      let queryParams: SearchParam = {
        ...this.queryParams,
        ...(city2 !== CITY2_DEFAULT ? {city: city2} : {}),
      };
      delete queryParams.index;

      this.router.navigate(['/dialect', 'region'], {queryParams}).then();
    }
    else {
      console.error('DialectComponent::onRegionChange() invalid city2 =', city2);
    }
  }

  onZoomRegion(region: REGION): void {
    console.log('DialectComponent::onZoomRegion() region =', region);

    if (this.selectedRegion === region) {
      return;
    }

    const queryParam: SearchParam = {
      ...(this.queryParams ? this.queryParams : {}),
    };
    if (region === REGION.ALL) {
      delete queryParam.region;
    }
    else {
      queryParam.region = GetRegionName(region) ?? '';
    }

    const basePath = this.router.url.split('?')[0];  // query parameter를 제거
    const urlWithParams = generateUrlWithQueryParams(basePath, queryParam);
    console.log('DialectComponent::onZoomRegion() urlWithParams =', urlWithParams);

    this.router.navigate(basePath.split('/'), {queryParams: queryParam}).then();
  }

  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // console.log('onScrollTop() ', target.scrollTop);
    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }

  protected onClickRope($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.redPipeSvc.Click($event);
  }
}
